import { useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format, parseISO, isValid } from "date-fns";
import { LoaderCircle, Pencil } from "lucide-react";
import ScheduleMaintenanceModal from "../common/ScheduleMaintenanceModal";
import MaintenanceOffCanvasSidebar from "../Wizards/MaintenanceOffCanvasSidebar";

export default function MaintenanceSchedulesCalendar({
  devices = [],
  onRefresh = async () => {},
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [currentView, setCurrentView] = useState("dayGridMonth");
  const [modalDeviceIds, setModalDeviceIds] = useState([]);

  const handleAdd = () => {
    setModalDeviceIds([]);
    setIsAdding(true);
  };

  const handleAfterCreate = async () => {
    await onRefresh();
    setIsAdding(false);
  };

  const getColorForDates = (start, end) => {
    const now = new Date();
    if (now < start) return "#3b82f6";
    if (end && now > end) return "#6b7280";
    return "#10b981";
  };

  const coerceISO = (v) => {
    if (!v) return null;
    if (typeof v === "string") {
      const d = parseISO(v);
      return isValid(d) ? d : null;
    }
    if (v instanceof Date && isValid(v)) return v;
    try {
      const d = parseISO(String(v));
      return isValid(d) ? d : null;
    } catch {
      return null;
    }
  };

  const parseScheduleWindow = (schedule) => {
    let attrs = schedule?.schedule_attributes;
    if (!attrs) return { start: null, end: null };

    if (typeof attrs === "string") {
      try {
        attrs = JSON.parse(attrs);
      } catch {
        attrs = { when: attrs };
      }
    }

    const start = coerceISO(attrs?.from);
    const end = coerceISO(attrs?.to);

    return { start, end };
  };

  const calendarEvents = useMemo(() => {
    const events = [];
    devices.forEach((dev) => {
      (dev.maintenance_schedules || []).forEach((ms, idx) => {
        const { start, end } = parseScheduleWindow(ms);
        if (!start) return;

        const color = getColorForDates(start, end || start);
        const title = `${dev.name || dev.id || "Device"}`;

        if (currentView === "dayGridMonth") {
          events.push({
            id: `${dev.id}-${idx}`,
            title,
            start: start.toISOString(),
            end: end ? end.toISOString() : undefined,
            backgroundColor: color + "1A",
            borderColor: color,
            textColor: "#000000",
            extendedProps: { device: dev, schedule: ms, start, end },
          });
        } else {
          const startDay = new Date(
            start.getFullYear(),
            start.getMonth(),
            start.getDate()
          );
          const endRef = end || start;
          const endDay = new Date(
            endRef.getFullYear(),
            endRef.getMonth(),
            endRef.getDate()
          );
          const totalDays = Math.max(
            0,
            Math.floor((endDay - startDay) / (1000 * 60 * 60 * 24))
          );

          events.push({
            id: `${dev.id}-${idx}-start`,
            title,
            start: start.toISOString(),
            end:
              totalDays > 0
                ? new Date(
                    startDay.getFullYear(),
                    startDay.getMonth(),
                    startDay.getDate() + 1
                  ).toISOString()
                : end
                ? end.toISOString()
                : undefined,
            backgroundColor: color + "1A",
            borderColor: color,
            textColor: "#000000",
            extendedProps: { device: dev, schedule: ms, start, end },
          });

          for (let i = 1; i < totalDays; i++) {
            const midDay = new Date(startDay);
            midDay.setDate(midDay.getDate() + i);
            events.push({
              id: `${dev.id}-${idx}-mid-${i}`,
              title: `[Continues] ${title}`,
              start: midDay.toISOString(),
              allDay: true,
              backgroundColor: color + "1A",
              borderColor: color,
              textColor: "#000000",
              extendedProps: { device: dev, schedule: ms, start, end },
            });
          }

          if (totalDays > 0) {
            const endStart = new Date(endDay);
            events.push({
              id: `${dev.id}-${idx}-end`,
              title,
              start: endStart.toISOString(),
              end: endRef.toISOString(),
              backgroundColor: color + "1A",
              borderColor: color,
              textColor: "#000000",
              extendedProps: { device: dev, schedule: ms, start, end },
            });
          }
        }
      });
    });
    return events;
  }, [devices, currentView]);

  const handleEventClick = (eventInfo) => {
    const { device, schedule, start, end } = eventInfo.event.extendedProps;
    const attrs =
      typeof schedule?.schedule_attributes === "string"
        ? (() => {
            try {
              return JSON.parse(schedule.schedule_attributes);
            } catch {
              return { when: schedule.schedule_attributes };
            }
          })()
        : schedule?.schedule_attributes || {};
    setModalItem({
      device,
      schedule,
      start,
      end,
      attrs,
    });
  };

  return (
    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-relative">
      <div className="lg:tw-col-span-3">
        <div>
          <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-px-6 tw-py-4">
            <h3 className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-flex tw-items-center tw-gap-2">
              Maintenance Schedules
            </h3>
            <button
              onClick={handleAdd}
              className="tw-inline-flex tw-items-center tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-medium tw-text-sm tw-px-4 tw-py-2 tw-rounded-md tw-shadow-sm tw-transition"
              disabled={isAdding}
            >
              {isAdding ? (
                <LoaderCircle className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white" />
              ) : (
                <>
                  <svg
                    className="tw-h-4 tw-w-4 tw-mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Maintenance
                </>
              )}
            </button>
          </div>

          <div className="tw-p-6 tw-pt-0">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              viewDidMount={(arg) => setCurrentView(arg.view.type)}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              events={calendarEvents}
              eventClick={handleEventClick}
              height="auto"
              eventDisplay="block"
              dayMaxEvents={3}
              moreLinkClick="day"
              selectable={true}
              selectMirror={true}
              eventAllDayDefault={false}
              dayHeaderFormat={{ weekday: "short" }}
              eventTimeFormat={{
                hour: "numeric",
                minute: "2-digit",
                meridiem: "short",
              }}
            />
          </div>
        </div>
      </div>

      {modalItem && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-lg tw-w-full tw-p-6 tw-relative">
            <button
              className="tw-absolute tw-top-2 tw-right-2 tw-text-gray-500 hover:tw-text-black"
              onClick={() => setModalItem(null)}
            >
              &times;
            </button>
            <h3 className="tw-text-lg tw-font-bold tw-mb-2 tw-flex tw-justify-between tw-items-center">
              <span>{modalItem.device?.name || modalItem.device?.id}</span>
            </h3>

            <hr className="tw-border tw-border-solid tw-border-black" />

            <div className="tw-text-sm tw-space-y-1">
              <div>
                <span className="tw-font-medium">Device:</span>
                {modalItem.device?.name || modalItem.device?.id}
              </div>
              {modalItem.start && (
                <div>
                  <span className="tw-font-medium">When/From:</span>{" "}
                  {format(modalItem.start, "PPpp")}
                </div>
              )}
              {modalItem.end && (
                <div>
                  <span className="tw-font-medium">To:</span>{" "}
                  {format(modalItem.end, "PPpp")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <MaintenanceOffCanvasSidebar
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        onCreated={handleAfterCreate}
        devices={devices}
      />
    </div>
  );
}
